<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createCloudinaryConfig } from "./fs";
  import { makeFileSystemCloudinary, testCloudinaryConnection } from "./fs-cloudinary";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createCloudinaryConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.cloudName.trim() != "" && config.apiKey.trim() != "" && config.apiSecret.trim() != "" && !testingConnection,
  );

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing cloudinary connection");
    try {
      config.cloudName = config.cloudName.trim();
      config.apiKey = config.apiKey.trim();
      config.apiSecret = config.apiSecret.trim();
      config.name = config.name.trim();
      await testCloudinaryConnection(config);
      if (config.name == "") {
        config.name = config.cloudName;
      }
      let fs = makeFileSystemCloudinary(config);
      let who = config.cloudName;
      telegramNotify(`New Cloudinary connection for ${who}`);
      captureEvent("cloudinary-login", {});
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Cloudinary">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-cloudinary-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-cloudinary-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-cloudinary-cloud">Cloud name:</label>
      <input
        type="text"
        id="input-cloudinary-cloud"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.cloudName}
      />

      <label class="mt-3" for="input-cloudinary-key">API key:</label>
      <input
        type="text"
        id="input-cloudinary-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.apiKey}
      />

      <label class="mt-3" for="input-cloudinary-secret">API secret:</label>
      <input
        type="password"
        id="input-cloudinary-secret"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.apiSecret}
      />

      <div class="mt-3 w-72 text-gray-500">
        All three are in the
        <a
          href="https://console.cloudinary.com/settings/api-keys"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">Cloudinary console</a
        >, under API keys. <a href="/doc/Cloudinary" class="text-blue-600 underline" target="_blank">More</a>
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Cloudinary</button>
    </div>
  {/snippet}
</DialogBase>
